import { Server, Shield, Clock, FileText, AlertTriangle, Cloud, TriangleAlert, ClipboardList, Boxes, History } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import StatusTimeline from './StatusTimeline';
import { AccessRequest } from '../types';
import { awsApi, azureApi, hetznerApi } from '../services/api';
import { Banner, Button, Card, Chip, Drawer, MonoCell, OutcomeBadge, Select, Timestamp } from '@/components/ds-zta';
import {
    expiryIfApprovedNow,
    formatDuration,
    grantedUntil,
    isOwnRequest,
    k8sScopeOf,
    normaliseStatus,
    requesterEmailOf,
    requesterNameOf,
    type AccessRequestRecord,
    type CurrentUserIdentity,
} from '@/features/access-requests/requestModel';

interface RequestDetailDrawerProps {
    request: AccessRequest;
    onClose: () => void;
    onApprove?: (request: AccessRequest, awsRoleArn?: string, azureRoleName?: string, hetznerPermissionName?: string) => void;
    onDeny?: (request: AccessRequest) => void;
    onRevoke?: (request: AccessRequest) => void;
    isAdmin: boolean;
    /** The signed-in user; "your request" is decided by their id, not their name. */
    currentUser?: CurrentUserIdentity | null;
    /** An approve or revoke for this request is in flight. */
    isActing?: boolean;
}

export default function RequestDetailDrawer({
    request,
    onClose,
    onApprove,
    onDeny,
    onRevoke,
    isAdmin,
    currentUser,
    isActing = false,
}: RequestDetailDrawerProps) {
    const record = request as AccessRequestRecord;
    const isSelfRequest = isOwnRequest(record, currentUser);
    const canActOnRequest = isAdmin && !isSelfRequest;
    const status = normaliseStatus(record.status);
    const requesterName = requesterNameOf(record);
    const requesterEmail = requesterEmailOf(record);
    const scope = k8sScopeOf(record);
    const until = grantedUntil(record);

    // "If approved now" moves with the clock, so it is recomputed every 30s
    // while a pending request is open.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (status !== 'pending') return;
        const t = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(t);
    }, [status]);

    const isAwsRequest = request.resource_type === 'aws_account';
    const isAzureRequest = (request.resource_type || '').startsWith('azure_');
    const isHetznerRequest = (request.resource_type || '').startsWith('hetzner_');
    const isCloudRequest = isAwsRequest || isAzureRequest || isHetznerRequest;
    const hasDescription = !!(request.metadata as any)?.aws_access_description || !!(request.metadata as any)?.azure_access_description || !!(request.metadata as any)?.hetzner_access_description;
    const cloudDescription = (request.metadata as any)?.aws_access_description || (request.metadata as any)?.azure_access_description || (request.metadata as any)?.hetzner_access_description || '';
    const cloudLabel = isHetznerRequest ? 'Hetzner' : isAzureRequest ? 'Azure' : 'AWS';
    const needsRoleAssignment = isCloudRequest && request.status === 'pending' && canActOnRequest;

    const [selectedRole, setSelectedRole] = useState<string>('');

    // Fetch available AWS roles for the role selector
    const { data: awsRoleMappingsData, isError: awsRolesError, refetch: refetchAwsRoles } = useQuery(
        'aws-role-mappings-for-approval',
        () => awsApi.listRoleMappings(),
        { enabled: needsRoleAssignment && isAwsRequest }
    );
    const awsRoles = awsRoleMappingsData?.data?.mappings || [];

    // Fetch available Azure roles for the role selector
    const { data: azureRoleMappingsData, isError: azureRolesError, refetch: refetchAzureRoles } = useQuery(
        'azure-role-mappings-for-approval',
        () => azureApi.listRoleMappings(),
        { enabled: needsRoleAssignment && isAzureRequest }
    );
    const azureRoles = azureRoleMappingsData?.data?.mappings || [];

    // Fetch available Hetzner permissions for the selector
    const { data: hetznerRoleMappingsData, isError: hetznerRolesError, refetch: refetchHetznerRoles } = useQuery(
        'hetzner-role-mappings-for-approval',
        () => hetznerApi.listRoleMappings(),
        { enabled: needsRoleAssignment && isHetznerRequest }
    );
    const hetznerPermissions = hetznerRoleMappingsData?.data?.mappings || [];

    const rolesError = isAwsRequest ? awsRolesError : isAzureRequest ? azureRolesError : isHetznerRequest ? hetznerRolesError : false;
    const retryRoles = () => {
        if (isAwsRequest) refetchAwsRoles();
        else if (isAzureRequest) refetchAzureRoles();
        else if (isHetznerRequest) refetchHetznerRoles();
    };

    const footer = (canActOnRequest && request.status === 'pending') ? (
        <div className="w-full space-y-3">
            {/* AWS Role Selector — only for AWS requests */}
            {needsRoleAssignment && (
                <div>
                    <Select
                        label={
                            <>
                                <Cloud className="h-3.5 w-3.5 inline mr-1" />
                                {isHetznerRequest ? 'Assign Hetzner Permission' : isAwsRequest ? 'Assign AWS Role' : 'Assign Azure Role'}
                            </>
                        }
                        className="w-full"
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                    >
                        <option value="">{isHetznerRequest ? 'Select Hetzner Permission...' : isAwsRequest ? 'Select AWS IAM Role...' : 'Select Azure RBAC Role...'}</option>
                        {isAwsRequest && awsRoles.map((m: any) => (
                            <option key={m.id} value={m.aws_role_arn}>
                                {m.aws_role_arn.split('/').pop()} — {m.aws_role_arn}
                            </option>
                        ))}
                        {isAzureRequest && azureRoles.map((m: any) => (
                            <option key={m.id} value={m.azure_role_name}>
                                {m.azure_role_name}{m.scope ? ` (${m.scope})` : ''}
                            </option>
                        ))}
                        {isHetznerRequest && hetznerPermissions.map((m: any) => (
                            <option key={m.id} value={m.permission_name}>
                                {m.permission_name}{m.api_token_scope ? ` (${m.api_token_scope})` : ''}
                            </option>
                        ))}
                    </Select>
                    {rolesError && (
                        <div className="mt-1 flex items-center gap-2">
                            <p className="flex items-center gap-1 text-micro text-[var(--danger-text)]"><AlertTriangle className="h-3 w-3 flex-shrink-0" aria-hidden="true" />Could not load the {isHetznerRequest ? 'permissions' : 'roles'}</p>
                            <Button variant="secondary" size="sm" onClick={retryRoles}>Retry</Button>
                        </div>
                    )}
                    {!selectedRole && (
                        <p className="mt-1 flex items-center gap-1 text-micro text-[var(--d2d-warning)]"><TriangleAlert className="h-3 w-3 flex-shrink-0" aria-hidden="true" />Select a role before approving</p>
                    )}
                </div>
            )}
            <div className="flex items-center gap-3">
                <Button
                    variant="secondary"
                    className="flex-1"
                    onClick={() => {
                        if (isAwsRequest) onApprove?.(request, selectedRole || undefined, undefined, undefined);
                        else if (isAzureRequest) onApprove?.(request, undefined, selectedRole || undefined, undefined);
                        else if (isHetznerRequest) onApprove?.(request, undefined, undefined, selectedRole || undefined);
                        else onApprove?.(request);
                    }}
                    disabled={(needsRoleAssignment && !selectedRole) || isActing}
                    loading={isActing}
                >
                    {needsRoleAssignment ? 'Approve with Role' : 'Approve'}
                </Button>
                <Button variant="danger" className="flex-1" onClick={() => onDeny?.(request)} disabled={isActing}>
                    Deny
                </Button>
            </div>
            <p className="text-micro text-[var(--d2d-text-muted)] text-center">
                Actions are logged and audited
            </p>
        </div>
    ) : (canActOnRequest && request.status === 'approved') ? (
        <Button
            variant="secondary"
            className="w-full text-[var(--d2d-warning)] border-[var(--zta-tint-warning-border)] hover:bg-[var(--zta-tint-warning)]"
            leadingIcon={<AlertTriangle className="h-4 w-4" />}
            onClick={() => onRevoke?.(request)}
            disabled={isActing}
            loading={isActing}
        >
            Revoke Access
        </Button>
    ) : (isSelfRequest && request.status === 'pending') ? (
        <div className="w-full text-center">
            <p className="text-sm font-medium text-[var(--d2d-text-primary)]">You can't approve your own request</p>
            <p className="text-xs text-[var(--d2d-text-muted)]">Another admin has to approve or deny it.</p>
        </div>
    ) : undefined;

    return (
        <Drawer
            open
            onClose={onClose}
            side="end"
            size="lg"
            dismissOnOverlay
            title="Request Details"
            description={<span className="inline-flex max-w-full items-center gap-1">ID: <MonoCell value={request.id} width="12rem" /></span>}
            footer={footer}
        >
            <div className="space-y-6">
            {/* Status Badge */}
            <div className="flex items-center justify-between">
                <OutcomeBadge value={request.status} shape="chip" decision className="capitalize">
                    {request.status}
                </OutcomeBadge>
                <span className="text-xs text-[var(--d2d-text-muted)]">
                    <Timestamp value={request.requested_at} noSeconds />
                </span>
            </div>

            {/* Requester */}
            <div className="space-y-3">
                <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase">
                    Requester
                </h4>
                <div className="flex items-center gap-3">
                    <div aria-hidden="true" className="flex items-center justify-center h-10 w-10 flex-shrink-0 rounded-xl bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)] font-bold text-sm uppercase">
                        {requesterName.charAt(0) || '?'}
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-[var(--d2d-text-primary)] break-words">
                            {requesterName}
                            {isSelfRequest && <span className="ml-1 text-micro font-normal text-[var(--d2d-text-muted)]">(You)</span>}
                        </p>
                        <p className="text-xs text-[var(--d2d-text-muted)] break-all">
                            {requesterEmail ?? <span className="italic">No email on the request</span>}
                        </p>
                    </div>
                </div>
            </div>

            {/* Resource */}
            <div className="space-y-3">
                <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase">
                    Resource
                </h4>
                <Card className="p-3 flex items-center gap-3">
                    <div aria-hidden="true" className="flex items-center justify-center h-9 w-9 flex-shrink-0 rounded-lg bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)]">
                        <Server className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-[var(--d2d-text-primary)] break-words">
                            {request.resource_name || request.resource_id || (request.request_type === 'role' ? `Role ${request.role_id ?? ''}`.trim() : request.resource_type)}
                        </p>
                        <p className="text-xs text-[var(--d2d-text-muted)] capitalize">
                            {request.request_type === 'role' ? 'Role access' : request.resource_type}
                        </p>
                    </div>
                </Card>
            </div>

            {/* K8s namespace scope (metadata.namespaces; ['*'] is cluster-wide) */}
            {scope && (
                <div className="space-y-3">
                    <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase flex items-center gap-1.5">
                        <Boxes className="h-3.5 w-3.5" aria-hidden="true" /> Kubernetes scope
                    </h4>
                    {scope.kind === 'cluster' ? (
                        <Banner variant="warning" title="Cluster-wide (all namespaces)">
                            Approving grants these permissions in every namespace of the cluster, including system namespaces.
                        </Banner>
                    ) : scope.kind === 'namespaces' ? (
                        <ul className="flex flex-wrap gap-1.5" aria-label="Namespaces">
                            {scope.namespaces.map((ns) => (
                                <li key={ns}><Chip variant="neutral" className="font-mono">{ns}</Chip></li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-[var(--d2d-text-muted)] italic">No namespace scope recorded on this request.</p>
                    )}
                </div>
            )}

            {/* Cloud Access Description (user's request) */}
            {hasDescription && (
                <div className="space-y-3">
                    <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase flex items-center gap-1.5">
                        <ClipboardList className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />{cloudLabel} Access Request
                    </h4>
                    <div className="rounded-lg bg-[var(--zta-tint-warning)] border border-[var(--zta-tint-warning-border)] p-3">
                        <p className="text-sm text-[var(--d2d-warning)] leading-relaxed">
                            {cloudDescription}
                        </p>
                    </div>
                </div>
            )}

            {/* AWS Role (assigned by admin — shown after approval) */}
            {(request.metadata as any)?.aws_role_arn && (
                <div className="space-y-3">
                    <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase flex items-center gap-1.5">
                        <Cloud className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />AWS IAM Role {(request.metadata as any)?.assigned_by_admin && <span className="text-micro font-normal text-[var(--d2d-text-muted)]">(assigned by admin)</span>}
                    </h4>
                    <div className="rounded-lg bg-[var(--zta-tint-warning)] border border-[var(--zta-tint-warning-border)] p-3">
                        <p className="text-sm font-semibold text-[var(--d2d-warning)]">
                            {((request.metadata as any).aws_role_arn as string).split('/').pop()}
                        </p>
                        <p className="text-xs text-[var(--d2d-warning)]/70 mt-1">
                            <MonoCell value={(request.metadata as any).aws_role_arn as string} />
                        </p>
                    </div>
                </div>
            )}

            {/* Azure Role (assigned by admin — shown after approval) */}
            {(request.metadata as any)?.azure_role_name && (
                <div className="space-y-3">
                    <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase flex items-center gap-1.5">
                        <Cloud className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />Azure Role {(request.metadata as any)?.assigned_by_admin && <span className="text-micro font-normal text-[var(--d2d-text-muted)]">(assigned by admin)</span>}
                    </h4>
                    <div className="rounded-lg bg-[var(--zta-tint-info)] border border-[var(--zta-tint-info-border)] p-3">
                        <p className="text-sm font-semibold text-[var(--d2d-info)]">
                            {(request.metadata as any).azure_role_name}
                        </p>
                    </div>
                </div>
            )}

            {/* Hetzner Permission (assigned by admin — shown after approval) */}
            {(request.metadata as any)?.hetzner_permission_name && (
                <div className="space-y-3">
                    <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase flex items-center gap-1.5">
                        <Server className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />Hetzner Permission {(request.metadata as any)?.assigned_by_admin && <span className="text-micro font-normal text-[var(--d2d-text-muted)]">(assigned by admin)</span>}
                    </h4>
                    <div className="rounded-lg bg-[var(--zta-tint-danger)] border border-[var(--zta-tint-danger-border)] p-3">
                        <p className="text-sm font-semibold text-[var(--danger-text)]">
                            {(request.metadata as any).hetzner_permission_name}
                        </p>
                    </div>
                </div>
            )}

            {/* Permissions */}
            <div className="space-y-3">
                <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase flex items-center gap-1.5">
                    <Shield className="h-3.5 w-3.5" /> Permissions
                </h4>
                <div className="flex flex-wrap gap-1.5">
                    {request.permissions.map((perm, i) => (
                        <Chip key={i} variant="neutral" className="capitalize">
                            {perm}
                        </Chip>
                    ))}
                </div>
            </div>

            {/* Duration */}
            <div className="space-y-3">
                <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" /> Duration
                </h4>
                <dl className="text-sm grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                    <dt className="text-[var(--d2d-text-muted)]">Requested</dt>
                    <dd className="text-[var(--d2d-text-primary)] tabular-nums">{formatDuration(request.duration_seconds)}</dd>
                    {status === 'pending' ? (
                        <>
                            <dt className="text-[var(--d2d-text-muted)]">If approved now</dt>
                            <dd className="text-[var(--d2d-text-primary)]">ends <Timestamp value={expiryIfApprovedNow(record, now)} noSeconds /></dd>
                        </>
                    ) : until && (status === 'approved' || status === 'expired' || status === 'revoked') ? (
                        <>
                            <dt className="text-[var(--d2d-text-muted)]">{status === 'approved' ? 'Access ends' : 'Window was until'}</dt>
                            <dd className="text-[var(--d2d-text-primary)]"><Timestamp value={until} noSeconds /></dd>
                        </>
                    ) : null}
                </dl>
            </div>

            {/* Justification */}
            <div className="space-y-3">
                <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" /> Justification
                </h4>
                <div className="rounded-lg bg-[var(--d2d-bg-secondary)] p-3 text-sm text-[var(--d2d-text-secondary)] leading-relaxed whitespace-pre-wrap break-words">
                    {request.justification || <span className="italic text-[var(--d2d-text-muted)]">No justification provided</span>}
                </div>
            </div>

            {/* History: created, decided by/at, revoked by/at, expired */}
            <div className="space-y-3">
                <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase flex items-center gap-1.5">
                    <History className="h-3.5 w-3.5" aria-hidden="true" /> Request history
                </h4>
                <Card className="p-4">
                    <StatusTimeline request={record} />
                </Card>
            </div>
            </div>
        </Drawer>
    );
}
